import { useEffect, useRef, useState } from "react";

import { AUTO_RELOAD_POLL_INTERVAL_MS } from "../constants";

const ACTIVITY_EVENT_NAMES = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"];

export const useIsUserAway = (visibleDelayMs: number, hiddenDelayMs: number): boolean => {
  const [isUserAway, setIsUserAway] = useState(false);
  const lastActivityAtRef = useRef(Date.now());
  const hiddenSinceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const recordActivity = (): void => {
      lastActivityAtRef.current = Date.now();
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now();
        return;
      }
      hiddenSinceRef.current = undefined;
      recordActivity();
    };

    if (document.visibilityState === "hidden") {
      hiddenSinceRef.current = Date.now();
    }

    for (const eventName of ACTIVITY_EVENT_NAMES) {
      document.addEventListener(eventName, recordActivity, { capture: true, passive: true });
    }
    window.addEventListener("scroll", recordActivity, { capture: true, passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const interval = setInterval(() => {
      const hiddenSince = hiddenSinceRef.current;
      const awayForMs = Date.now() - (hiddenSince ?? lastActivityAtRef.current);
      const requiredDelayMs = hiddenSince === undefined ? visibleDelayMs : hiddenDelayMs;
      setIsUserAway(awayForMs >= requiredDelayMs);
    }, AUTO_RELOAD_POLL_INTERVAL_MS);

    return () => {
      for (const eventName of ACTIVITY_EVENT_NAMES) {
        document.removeEventListener(eventName, recordActivity, { capture: true });
      }
      window.removeEventListener("scroll", recordActivity, { capture: true });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [hiddenDelayMs, visibleDelayMs]);

  return isUserAway;
};
